'use client';

import { useRef } from 'react';

import ChevronIcon from '@/fo/icons/base/icons/chevron24.svg';
import { Option, UiKitListboxBase } from '@/fo/ui/kit/listbox/base';

import { ChipsInput } from '../components/ChipsInput/ChipsInput';
import { useMultipleSelect } from '../hooks/useMultipleSelect';

import { styles } from './UiKitSelectMultiple.styles';

export interface UiKitSelectMultipleProps<T extends Option> {
  options: T[];
  selectedOptions?: T[];
  onChange?: (selectedOptions: T[]) => void;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  helperText?: string;
  errorMessage?: string;
  className?: string;
  chipClassName?: string;
  chipInputClassName?: string;
}

export function UiKitSelectMultiple<T extends Option>({
  options,
  onChange,
  disabled,
  label,
  placeholder,
  helperText,
  errorMessage,
  className,
  chipClassName,
  chipInputClassName
}: UiKitSelectMultipleProps<T>) {
  const slots = styles();
  const triggerRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);

  const {
    inputValue,
    isOpen,
    filteredOptions,
    highlightedIndex,
    selectedOptions,
    handleRemoveOption,
    handleSelectionChange,
    getToggleButtonProps,
    getMenuProps,
    getInputProps,
    getItemProps,
    getDropdownProps
  } = useMultipleSelect({
    options,
    onChange
  });

  return (
    <div className={slots.base({ className })}>
      <ChipsInput
        wrapperRef={triggerRef}
        {...getInputProps(getDropdownProps({ preventKeyAction: isOpen }))}
        selectedOptions={selectedOptions}
        onRemoveOption={handleRemoveOption}
        inputValue={inputValue}
        placeholder={placeholder}
        value={inputValue}
        label={label}
        helperText={helperText}
        errorMessage={errorMessage}
        disabled={disabled}
        chipClassName={chipClassName}
        className={chipInputClassName}
        endAdornment={
          <button
            {...getToggleButtonProps(
              getDropdownProps({ preventKeyAction: isOpen })
            )}
            className={slots.button()}
            disabled={disabled}
            type="button"
          >
            <ChevronIcon className={slots.icon({ open: isOpen })} />
          </button>
        }
      />

      <UiKitListboxBase
        isOpen={isOpen}
        options={filteredOptions}
        triggerRef={triggerRef}
        trigger="combobox"
        selectionMode="multiple"
        placement="bottom"
        isNonModal
        selectedKeys={selectedOptions.map((item: T) => item.value)}
        onSelectionChange={handleSelectionChange}
        renderListBox={(options, optionClassName) => {
          const menuProps = getMenuProps({ ref: menuRef });
          return (
            <ul
              className=""
              {...menuProps}
            >
              {options.map((option, index) => (
                <li
                  className={optionClassName}
                  key={option.value}
                  {...getItemProps({ item: option, index })}
                  data-focus={highlightedIndex === index}
                >
                  {option.label}
                </li>
              ))}
            </ul>
          );
        }}
      />
    </div>
  );
}
